import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
export interface Size {
  width: number
  height: number
}

/**
 * `useElementSize` is a hook to measure the size of a DOM element.
 * It tracks the width and height of the element and updates them on window resize or element changes.
 *
 */

function useElementSize<T extends HTMLElement = HTMLDivElement>(): { setRef: (node: T | null) => void; size: Size } {
  const [ref, setRef] = useState<T | null>(null)
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  const handleSize = useCallback(() => {
    if (ref) {
      setWidth(ref.offsetWidth)
      setHeight(ref.offsetHeight)
    }
  }, [ref])

  const useEnvironmentEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

  useEnvironmentEffect(() => {
    if (!ref) {
      return void 0
    }

    handleSize()

    const resizeObserver = new ResizeObserver(handleSize)
    resizeObserver.observe(ref)

    return () => resizeObserver.disconnect()
  }, [ref, handleSize])

  return {
    setRef,
    size: { width, height } as Size
  }
}

export { useElementSize }
